// THE LONG KINDLING — Room 3: "The Bakehouse" (Act I) — the Hush; bring the poor old man in.
// Authored from research/wall/script/v3/rooms_01-05.md §Room 3 and BUILD_PLAN.md, copying the proven
// hearth_hall.ts pattern (field names, fx/fire wiring).
//
// Painting public/art/bakehouse.png is 384x216; the room is 480x270 (ART→ROOM x1.25). The OVEN fire
// CUTOUT (art/bakehouse_fire.png, baked by tools/gen_firemask.mjs over the domed oven-mouth) is
// twinkled automatically (renderer derives `<bgImage>`→`_fire.png`).
//
// No mechanical lock: the "puzzle" is social (meet the Hush, feel the pull). The warm crust is a take
// that sets the global `ate_crust` sympathy-lean (never a punish, no hunger meter). Garrow the baker
// LEADS the Hush — his creed is the SPINE mercy: bring the poor old man in.
import { loadRoom, type Room } from "../../core/schema";

export const bakehouse = {
  id: "bakehouse",
  name: "The Bakehouse",
  size: { w: 480, h: 270 },
  background: { color: 0x1a120a },
  bgImage: "art/bakehouse.png",
  // hero starts on the floured floor, near the door (the child's drawing is pinned by it)
  spawn: { x: 320, y: 244 },
  actorRadius: 8,
  interactRadius: 64,
  state: {},
  walkmesh: [
    [ { x: 40, y: 224 }, { x: 452, y: 224 }, { x: 452, y: 266 }, { x: 36, y: 266 } ],
  ],
  hotspots: [
    // ---- the oven — a big bright fire indoors is GOOD (reinforces R2: mercy is aimed)
    { id: "oven", verb: "kindle", label: "the oven", interactAt: { x: 240, y: 242 },
      line: "Big as a cave-mouth and twice as kind. Garrow keeps it roaring — says a cold bakehouse is a cold heart. Indoors, in the warm, a big bright fire is good and right. The grim fire is only the one watch-fire out at the trees.",
      effect: { flame: 1 },
      polygon: [ { x: 208, y: 110 }, { x: 276, y: 110 }, { x: 276, y: 196 }, { x: 208, y: 196 } ] },

    // ---- the cooling loaves / warm crust — the small first Hush-aligning choice (take, global sympathy-lean)
    { id: "warm_crust", verb: "take", label: "the warm crust", interactAt: { x: 248, y: 244 },
      line: "Fresh loaves, the crust still ticking as it cools. Garrow tears the heel off and presses it on you. Hard to say no to warm bread. You take it and eat — and a little of you leans, just then, toward the kind people's read.",
      effect: { setGlobal: { ate_crust: true } },
      polygon: [ { x: 224, y: 192 }, { x: 280, y: 192 }, { x: 280, y: 216 }, { x: 224, y: 216 } ] },

    // ---- the child's crayon drawing — the room's one wrong note: the watcher drawn home and smiling
    { id: "childs_drawing", verb: "examine", label: "the child's drawing", interactAt: { x: 348, y: 244 },
      line: "A little one's done it — the tall man at the trees with his back to us, and then drawn him again turned round and smiling, the one that keeps us warm, holding a child's hand by the fire. They've given him a face. Out where he stands there's no face turned to us at all, and never will be. They've drawn him home, and they don't know what they're wishing.",
      polygon: [ { x: 326, y: 120 }, { x: 372, y: 120 }, { x: 372, y: 170 }, { x: 326, y: 170 } ] },

    // ---- the flour-bin & tally — the counting-in-fours recurs (look-at only)
    { id: "flour_tally", verb: "examine", label: "the flour-bin & tally", interactAt: { x: 200, y: 244 },
      line: "Flour measured out in fours with one knock over — same count as the hens' seed up the granary, same count Mother pours the door-bowl by. Garrow does it without thinking, the way his father did.",
      polygon: [ { x: 176, y: 190 }, { x: 222, y: 190 }, { x: 222, y: 218 }, { x: 176, y: 218 } ] },
  ],
  // living-scene fx: the domed oven-mouth breathes heat (glow+flames+embers over the baked cutout).
  // oven fire at ART(193,92) -> ROOM(241,115).
  fx: [
    { id: "oven_glow", kind: "glow", layer: "back", x: 217, y: 100, w: 48, h: 36, color: 0xff9a40, intensity: 1.0, seed: 1 },
    { id: "oven_flames", kind: "flames", layer: "back", x: 227, y: 104, w: 28, h: 26, intensity: 0.95, seed: 2 },
    { id: "oven_embers", kind: "embers", layer: "front", x: 231, y: 98, w: 24, h: 34, count: 10, intensity: 0.9, seed: 3 },
  ],
  npcs: [
    // Garrow the baker — LEADER of the Hush; his creed is the SPINE mercy: bring the poor old man in.
    { id: "garrow", x: 244, y: 240, radius: 9, color: 0x9c8466, label: "Garrow the baker",
      dialog: [
        { text: "I'm that tired, lad. Tired of baking bread for a war nobody can name. We've bread, we've each other, we've a warm fire and a cold chair beside it. Bring the poor old man in, I say. Sit him down at our table, give him a heel of warm bread, let him be a person again. He's old, he's cold, he's ours. Where in God's name is the harm in that.", mood: "sad" },
        { text: "Two men lost on the relay last winter, holding that line. Two. For what — so an old man can freeze at a fire we won't let him sit at? You tell me that watch is worth two good young men and I'll call you hard as the wardens.", mood: "wary" },
        { text: "You take a heel of bread, now. Go on. Nobody ever got warmer turning down warm bread.", mood: "warm" },
      ] },
  ],
  // Exit to the Green & Well (R7) — wired when R7 lands; commented so the world boots clean.
  exits: [
    // the door -> the Green & Well (R7)
    { id: "to_green", target: "green_well", spawn: { x: 240, y: 244 }, transition: "fade",
      polygon: [ { x: 296, y: 226 }, { x: 348, y: 226 }, { x: 348, y: 250 }, { x: 296, y: 250 } ] },
  ],
};

export const room: Room = loadRoom(bakehouse);
